import { useEffect, useRef, useState } from "react";
import html2canvas from "html2canvas";
import api, { rupiah, errMsg, exportReport } from "../lib/api";
import Modal, { Field } from "../components/Modal";
import { Plus, Trash2, ShoppingCart, FileText, Pencil, XCircle, Search, MessageCircle, Printer, FileDown } from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "../context/AuthContext";

const today = () => new Date().toISOString().slice(0, 10);
const fmtSent = (iso) => { try { return new Date(iso).toLocaleString("id-ID", { day: "2-digit", month: "short", hour: "2-digit", minute: "2-digit" }); } catch { return ""; } };
const emptyLine = () => ({ name: "", item_id: "", qty: 1, pot_mp: 0, disc: 0, extra_bubble: 0, pajak_pct: 0, price: "", buy_price: "" });
const STORE = {
  name: "Mainan Dede",
  address: "Golf Lake Rukan Paris blok A30-31, Cengkareng Timur, Jakarta Barat",
  phone: "081399991457",
  bank: "BCA 6370271222 a.n. Felly Rusli",
};

// Searchable dropdown for item name (typeahead) — works inside scrolling modal
const NameCombo = ({ value, names, onSelect, testid }) => {
  const [open, setOpen] = useState(false);
  const [q, setQ] = useState("");
  const ref = useRef(null);
  useEffect(() => {
    const h = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener("mousedown", h);
    return () => document.removeEventListener("mousedown", h);
  }, []);
  const filtered = names.filter((n) => n.toLowerCase().includes(q.trim().toLowerCase()));
  return (
    <div className="relative" ref={ref}>
      <div className="relative">
        <Search className="w-4 h-4 absolute left-2.5 top-1/2 -translate-y-1/2 text-[#52525B] pointer-events-none" />
        <input
          data-testid={testid}
          className="nb-input w-full pl-8"
          placeholder="Cari / pilih barang…"
          value={open ? q : (value || "")}
          onFocus={() => { setOpen(true); setQ(""); }}
          onChange={(e) => { setQ(e.target.value); setOpen(true); }}
        />
      </div>
      {open && (
        <div data-testid={`${testid}-list`} className="absolute z-30 mt-1 w-full max-h-56 overflow-y-auto bg-white border-2 border-[#0A0A0A] rounded-lg shadow-[3px_3px_0_#0A0A0A]">
          {filtered.length === 0 ? (
            <div className="px-3 py-2 text-sm text-[#52525B]">Tidak ada barang</div>
          ) : filtered.map((n) => (
            <button
              type="button"
              key={n}
              onClick={() => { onSelect(n); setOpen(false); setQ(""); }}
              className={`w-full text-left px-3 py-2 text-sm border-b border-[#0A0A0A]/10 hover:bg-[var(--primary)] ${n === value ? "font-bold bg-[var(--primary)]" : ""}`}
            >
              {n}
            </button>
          ))}
        </div>
      )}
    </div>
  );
};

export default function Sales() {
  const { user } = useAuth();
  const canEdit = !!(user?.is_owner || user?.perms?.penjualan_edit);
  const canHpp = !!(user?.is_owner || user?.perms?.modal);
  const [sales, setSales] = useState([]);
  const [items, setItems] = useState([]);
  const [open, setOpen] = useState(false);
  const [editId, setEditId] = useState(null);
  const [date, setDate] = useState(today());
  const [customer, setCustomer] = useState("");
  const [tipe, setTipe] = useState("Offline");
  const [lines, setLines] = useState([emptyLine()]);
  const [notaOpen, setNotaOpen] = useState(false);
  const [notaSale, setNotaSale] = useState(null);
  const [buyerPhone, setBuyerPhone] = useState("");
  const [customerPhone, setCustomerPhone] = useState("");
  const [sharing, setSharing] = useState(false);
  const [q, setQ] = useState("");
  const [from, setFrom] = useState("");
  const [to, setTo] = useState("");
  const notaRef = useRef(null);

  const load = () => {
    api.get("/sales").then((r) => setSales(r.data)).catch((e) => toast.error(errMsg(e)));
    api.get("/items").then((r) => setItems(r.data));
  };
  useEffect(() => { load(); }, []);

  const itemMap = Object.fromEntries(items.map((i) => [i.id, i]));
  // group items by NORMALIZED name (trim + collapse spaces + case-insensitive)
  // so the same product name appears only ONCE; all its colors go under one entry
  const norm = (s) => (s || "").trim().replace(/\s+/g, " ");
  const groups = {};
  items.forEach((i) => {
    const disp = norm(i.name);
    const key = disp.toLowerCase();
    if (!groups[key]) groups[key] = { display: disp, items: [] };
    groups[key].items.push(i);
  });
  const variantsFor = (name) => (groups[norm(name).toLowerCase()]?.items) || [];
  const canonName = (name) => groups[norm(name).toLowerCase()]?.display || norm(name);
  const names = Object.values(groups).map((g) => g.display).sort((a, b) => a.localeCompare(b));

  const linePrice = (l) => { const dp = itemMap[l.item_id]?.sell_price || 0; return (l.price === "" || l.price == null) ? dp : Number(l.price); };
  const grossSum = lines.reduce((s, l) => s + linePrice(l) * Number(l.qty || 0), 0);
  const linePajak = (l) => Math.round(Math.max(0, linePrice(l) * Number(l.qty || 0) + Number(l.extra_bubble || 0) - Number(l.disc || 0)) * Number(l.pajak_pct || 0) / 100);
  const potSum = lines.reduce((s, l) => s + Number(l.pot_mp || 0), 0);
  const discSum = lines.reduce((s, l) => s + Number(l.disc || 0), 0);
  const extraSum = lines.reduce((s, l) => s + Number(l.extra_bubble || 0), 0);
  const pajakSum = lines.reduce((s, l) => s + linePajak(l), 0);
  const netSum = grossSum + extraSum - potSum - discSum - pajakSum;

  const setLine = (idx, patch) => setLines(lines.map((l, i) => (i === idx ? { ...l, ...patch } : l)));
  const onNameChange = (idx, name) => {
    const variants = variantsFor(name);
    // auto-pick when only one color variant exists
    setLine(idx, { name: canonName(name), item_id: variants.length === 1 ? variants[0].id : "" });
  };
  const addLine = () => setLines([...lines, emptyLine()]);
  const removeLine = (idx) => setLines(lines.filter((_, i) => i !== idx));

  const resetForm = () => {
    setEditId(null); setLines([emptyLine()]); setCustomer(""); setCustomerPhone(""); setDate(today()); setTipe("Offline");
  };
  const openCreate = () => { resetForm(); setOpen(true); };
  const doExport = async (fmt) => {
    try { await exportReport("sales", fmt, { start: from, end: to, filename: "laporan_penjualan" }); toast.success("Laporan diunduh"); }
    catch (e) { toast.error(errMsg(e)); }
  };
  const openEdit = (s) => {
    setEditId(s.id);
    setDate(String(s.date).slice(0, 10));
    setCustomer(s.customer || "");
    setCustomerPhone(s.customer_phone || "");
    setTipe(s.tipe || "Offline");
    setLines(s.lines.map((l) => ({ name: canonName(l.item_name), item_id: l.item_id, qty: l.qty, pot_mp: l.pot_mp || 0, disc: l.disc || 0, extra_bubble: l.extra_bubble || 0, pajak_pct: l.pajak_pct || 0, price: l.price ?? "", buy_price: l.hpp_override ?? "" })));
    setOpen(true);
  };

  const save = async (e) => {
    e.preventDefault();
    const valid = lines.filter((l) => l.item_id && Number(l.qty) > 0);
    if (!valid.length) return toast.error("Tambahkan minimal 1 barang");
    const payload = { date, customer, customer_phone: customerPhone, tipe, lines: valid.map((l) => ({ item_id: l.item_id, qty: Number(l.qty), pot_mp: Number(l.pot_mp) || 0, disc: Number(l.disc) || 0, extra_bubble: Number(l.extra_bubble) || 0, pajak_pct: Number(l.pajak_pct) || 0, price: (l.price === "" || l.price == null) ? null : Number(l.price), buy_price: (!canHpp || l.buy_price === "" || l.buy_price == null) ? null : Number(l.buy_price) })) };
    try {
      if (editId) {
        await api.put(`/sales/${editId}`, payload);
        toast.success("Penjualan diperbarui, stok disesuaikan");
      } else {
        await api.post("/sales", payload);
        toast.success("Penjualan tercatat, stok otomatis berkurang");
      }
      setOpen(false); resetForm(); load();
    } catch (err) { toast.error(errMsg(err)); }
  };

  const cancelSale = async (s) => {
    if (!window.confirm(`Cancel / hapus nota ${s.customer || "Umum"} (${rupiah(s.net ?? s.total)})? Stok akan dikembalikan.`)) return;
    try {
      await api.delete(`/sales/${s.id}`);
      toast.success("Penjualan dibatalkan, stok dikembalikan");
      load();
    } catch (err) { toast.error(errMsg(err)); }
  };

  const openNota = (s) => { setNotaSale(s); setBuyerPhone(s.customer_phone || ""); setNotaOpen(true); };
  const buildNotaText = (s) => {
    const rows = s.lines.map((l, i) => `${i + 1}. ${l.item_name}${l.warna ? ` (${l.warna})` : ""} x${l.qty} — ${rupiah(l.subtotal)}`).join("\n");
    return `🧸 *MAINAN DEDE*\n${STORE.address}\nTelp: ${STORE.phone}\n\n🧾 Nota ${s.date}\nPelanggan: ${s.customer || "Umum"}\nVia: ${s.tipe || "Offline"}\n\n${rows}\n\n*TOTAL: ${rupiah(s.net ?? s.total)}*\n\n💳 Pembayaran:\n${STORE.bank}\n\nTerima kasih sudah berbelanja 🙏`;
  };
  const waDigits = () => buyerPhone.replace(/\D/g, "").replace(/^0/, "62");
  const markSent = async () => {
    if (!notaSale) return;
    try {
      const r = await api.patch(`/sales/${notaSale.id}/mark-sent`);
      setNotaSale(r.data);
      setSales((prev) => prev.map((x) => (x.id === r.data.id ? r.data : x)));
    } catch (_) { /* silent */ }
  };
  // Render the aesthetic nota as a PNG and share it (photo) via WhatsApp.
  const shareNotaImage = async () => {
    if (!notaSale || !notaRef.current) return;
    setSharing(true);
    try {
      const canvas = await html2canvas(notaRef.current, { scale: 2, backgroundColor: "#FFFDF7", useCORS: true });
      const blob = await new Promise((res) => canvas.toBlob(res, "image/png"));
      if (!blob) throw new Error("render gagal");
      const file = new File([blob], `nota-mainan-dede-${notaSale.date}.png`, { type: "image/png" });
      const digits = waDigits();
      if (navigator.canShare && navigator.canShare({ files: [file] })) {
        await navigator.share({ files: [file], title: "Nota Mainan Dede", text: `Nota Mainan Dede ${notaSale.date}` });
      } else {
        const url = URL.createObjectURL(blob);
        const a = document.createElement("a");
        a.href = url; a.download = file.name; a.click();
        URL.revokeObjectURL(url);
        toast.success("Foto nota tersimpan. Lampirkan ke chat WhatsApp lalu kirim.");
        window.open(`https://wa.me/${digits}?text=${encodeURIComponent(buildNotaText(notaSale))}`, "_blank");
      }
      await markSent();
    } catch (err) {
      if (err && err.name === "AbortError") return; // user cancelled share sheet
      toast.error("Gagal membuat foto nota");
    } finally {
      setSharing(false);
    }
  };
  // Send nota as text message via WhatsApp (fallback, no photo).
  const sendWAText = () => {
    if (!notaSale) return;
    window.open(`https://wa.me/${waDigits()}?text=${encodeURIComponent(buildNotaText(notaSale))}`, "_blank");
    markSent();
  };

  const printReceipt = (s) => {
    const bersih = s.net ?? s.total;
    const rows = s.lines.map((l, i) => {
      const unit = l.price != null ? l.price : (l.qty ? ((l.harga || l.subtotal) / l.qty) : 0);
      const disc = (l.pot_mp || 0) + (l.disc || 0);
      return `<tr><td class="l">${i + 1}</td><td class="l">${l.item_name}${l.warna ? ` (${l.warna})` : ""}</td><td class="c">${l.qty}</td><td>${rupiah(unit)}</td><td>${disc ? rupiah(disc) : "-"}</td><td>${rupiah(l.subtotal)}</td></tr>`;
    }).join("");
    const html = `<!doctype html><html><head><meta charset="utf-8"><title>Nota ${s.date}</title>
      <link href="https://fonts.googleapis.com/css2?family=Poppins:wght@400;600;700&family=Quicksand:wght@600;700&display=swap" rel="stylesheet">
      <style>
        *{box-sizing:border-box}
        body{font-family:'Poppins',sans-serif;color:#2B2B2B;margin:0;padding:18px;background:#FFFDF7}
        .card{max-width:380px;margin:0 auto;background:#FFFDF7;border:2px solid #0A0A0A;border-radius:18px;overflow:hidden}
        .head{background:#E9C46A;text-align:center;padding:16px 12px}
        .head img{height:54px;max-width:80%}
        .name{font-family:'Quicksand',sans-serif;font-weight:700;font-size:22px;text-align:center;margin:12px 0 3px}
        .info{text-align:center;font-size:11px;color:#7a7a7a;line-height:1.5;padding:0 18px}
        .meta{display:flex;justify-content:space-between;font-size:11px;color:#777;padding:12px 18px 0}
        .wrap{padding:4px 18px}
        table{width:100%;border-collapse:collapse;font-size:11px;margin:6px 0}
        thead th{border-top:2px solid #0A0A0A;border-bottom:2px solid #0A0A0A;padding:6px 3px;text-align:right;font-weight:700}
        thead th.l{text-align:left}thead th.c{text-align:center}
        td{padding:6px 3px;border-bottom:1px solid #eadfc6;text-align:right}
        td.l{text-align:left}td.c{text-align:center}
        .total{display:flex;justify-content:space-between;align-items:center;padding:10px 18px;border-top:2px solid #0A0A0A}
        .total .lbl{font-weight:700}.total b{font-family:'Quicksand',sans-serif;font-size:19px}
        .pay{margin:14px 18px;background:#fff;border:2px dashed #0A0A0A;border-radius:12px;padding:10px 12px;font-size:12px;line-height:1.6}
        .thanks{text-align:center;font-size:11px;color:#999;padding:0 0 18px}
      </style></head><body>
      <div class="card">
        <div class="head"><img src="${window.location.origin}/logo.png" onerror="this.style.display='none'"/></div>
        <div class="name">${STORE.name}</div>
        <div class="info">${STORE.address}<br>Telp: ${STORE.phone}</div>
        <div class="meta"><span>🗓️ ${s.date}</span><span>${s.customer || "Umum"} · ${s.tipe || "Offline"}</span></div>
        <div class="wrap"><table>
          <thead><tr><th class="l">No</th><th class="l">Nama Barang</th><th class="c">Qty</th><th>Harga</th><th>Disc</th><th>Total</th></tr></thead>
          <tbody>${rows}</tbody></table></div>
        <div class="total"><span class="lbl">TOTAL</span><b>${rupiah(bersih)}</b></div>
        <div class="pay"><b>💳 Pembayaran</b><br>${STORE.bank}</div>
        <div class="thanks">Terima kasih sudah berbelanja 🙏</div>
      </div>
      <script>window.onload=function(){setTimeout(function(){window.print()},450)}</script>
      </body></html>`;
    const w = window.open("", "_blank", "width=420,height=720");
    if (!w) { toast.error("Popup diblokir browser, izinkan popup dulu"); return; }
    w.document.write(html); w.document.close();
  };

  const todaySales = sales.filter((s) => String(s.date).startsWith(today()));
  const todayTotal = todaySales.reduce((s, x) => s + x.total, 0);

  const qq = q.trim().toLowerCase();
  const lineMatch = (l) => `${l.item_name} ${l.warna || ""}`.toLowerCase().includes(qq);
  const inDate = (s) => { const d = String(s.date).slice(0, 10); return (!from || d >= from) && (!to || d <= to); };
  const shownSales = sales.filter((s) => inDate(s) && (!qq || s.lines.some(lineMatch)));
  const filtering = !!(qq || from || to);
  const matchQty = qq
    ? shownSales.reduce((sum, s) => sum + s.lines.filter(lineMatch).reduce((a, l) => a + Number(l.qty || 0), 0), 0)
    : 0;
  const matchOmzet = qq
    ? shownSales.reduce((sum, s) => sum + s.lines.filter(lineMatch).reduce((a, l) => a + Number(l.subtotal || 0), 0), 0)
    : 0;

  return (
    <div className="space-y-5 animate-fadeup">
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <div>
          <h1 className="text-2xl sm:text-3xl font-display font-extrabold">Penjualan Harian</h1>
          <p className="text-[#52525B] text-sm">Stok otomatis dikurangi tiap penjualan</p>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          <button data-testid="export-sales-excel" onClick={() => doExport("excel")} className="nb-btn px-3 py-2.5 bg-white text-sm flex items-center gap-1.5"><FileDown className="w-4 h-4" /> Excel</button>
          <button data-testid="export-sales-pdf" onClick={() => doExport("pdf")} className="nb-btn px-3 py-2.5 bg-white text-sm flex items-center gap-1.5"><FileDown className="w-4 h-4" /> PDF</button>
          <button data-testid="add-sale-button" onClick={openCreate} className="nb-btn px-4 py-2.5 flex items-center gap-2 text-[#0A0A0A]" style={{ background: "var(--primary)" }}>
            <Plus className="w-5 h-5" strokeWidth={2.5} /> Catat Penjualan
          </button>
        </div>
      </div>

      <div className="nb-card p-5" style={{ background: "var(--masuk)" }} data-testid="today-sales-summary">
        <span className="label-tag">Total Penjualan Hari Ini ({todaySales.length} nota)</span>
        <div className="font-display font-extrabold text-3xl mt-1">{rupiah(todayTotal)}</div>
      </div>

      <div className="space-y-2">
        <div className="relative">
          <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-[#52525B]" />
          <input data-testid="sales-search-item" value={q} onChange={(e) => setQ(e.target.value)} placeholder="Cari barang — lihat tanggal penjualannya…" className="nb-input pl-9" />
          {qq && (
            <button data-testid="sales-search-clear" onClick={() => setQ("")} className="absolute right-2 top-1/2 -translate-y-1/2 w-7 h-7 flex items-center justify-center text-[#52525B]"><XCircle className="w-4 h-4" /></button>
          )}
        </div>
        <div className="flex items-end gap-2 flex-wrap">
          <div>
            <label className="label-tag block mb-1">Dari tanggal</label>
            <input data-testid="sales-date-from" type="date" value={from} onChange={(e) => setFrom(e.target.value)} className="nb-input w-auto" />
          </div>
          <div>
            <label className="label-tag block mb-1">Sampai tanggal</label>
            <input data-testid="sales-date-to" type="date" value={to} onChange={(e) => setTo(e.target.value)} className="nb-input w-auto" />
          </div>
          {filtering && (
            <button data-testid="sales-filter-reset" onClick={() => { setFrom(""); setTo(""); setQ(""); }} className="nb-btn px-3 py-2 bg-white text-sm">Reset</button>
          )}
        </div>
        {filtering && (
          <div data-testid="sales-search-result" className="nb-card p-3 text-sm" style={{ background: "var(--primary)" }}>
            {qq ? <>"<b>{q.trim()}</b>" terjual <b>{matchQty} pcs</b> di </> : <>Ditemukan </>}
            <b>{shownSales.length} nota</b>
            {qq ? <> • omzet <b>{rupiah(matchOmzet)}</b></> : <> • total <b>{rupiah(shownSales.reduce((a, s) => a + (s.net ?? s.total), 0))}</b></>}
            {(from || to) && <> • {from || "…"} s/d {to || "…"}</>}
          </div>
        )}
      </div>

      {shownSales.length === 0 ? (
        <div className="nb-card p-10 text-center">
          <ShoppingCart className="w-10 h-10 mx-auto mb-3 text-[#52525B]" />
          <p className="text-[#52525B]">{filtering ? "Tidak ada penjualan untuk filter ini." : "Belum ada penjualan."}</p>
        </div>
      ) : (
        <div className="space-y-3">
          {shownSales.map((s) => (
            <div key={s.id} data-testid={`sale-row-${s.id}`} className="nb-card p-4">
              <div className="flex items-center justify-between">
                <div>
                  <div className="font-bold">{s.customer || "Umum"}</div>
                  <span className="label-tag">{s.date} • {s.tipe || "Offline"}</span>
                  {s.edited_by && (
                    <span data-testid={`sale-edited-${s.id}`} className="inline-flex items-center gap-1 ml-2 text-[10px] font-bold uppercase tracking-wide px-1.5 py-0.5 border-2 border-[#0A0A0A] rounded align-middle" style={{ background: "var(--primary)" }}>
                      <Pencil className="w-3 h-3" /> diedit oleh {s.edited_by}
                    </span>
                  )}
                  {s.sent_at && (
                    <span data-testid={`sale-sent-${s.id}`} className="inline-flex items-center gap-1 ml-2 text-[10px] font-bold uppercase tracking-wide px-1.5 py-0.5 border-2 border-[#0A0A0A] rounded align-middle text-white" style={{ background: "#25D366" }}>
                      <MessageCircle className="w-3 h-3" /> terkirim {fmtSent(s.sent_at)}
                    </span>
                  )}
                </div>
                <div className="text-right">
                  <div className="font-display font-extrabold text-lg">{rupiah(s.net ?? s.total)}</div>
                  <div className="text-xs text-[#52525B]">kotor {rupiah(s.total)}{s.potongan ? ` · pot ${rupiah(s.potongan)}` : ""}{s.disc_total ? ` · disc ${rupiah(s.disc_total)}` : ""}</div>
                  <div className="flex items-center justify-end gap-1.5 mt-1 flex-wrap">
                    <button data-testid={`sale-nota-${s.id}`} onClick={() => openNota(s)} className="nb-btn px-2 py-1 text-xs bg-white inline-flex items-center gap-1"><FileText className="w-3.5 h-3.5" /> Nota</button>
                    {canEdit && (
                      <>
                        <button data-testid={`sale-edit-${s.id}`} onClick={() => openEdit(s)} className="nb-btn px-2 py-1 text-xs bg-white inline-flex items-center gap-1"><Pencil className="w-3.5 h-3.5" /> Edit</button>
                        <button data-testid={`sale-cancel-${s.id}`} onClick={() => cancelSale(s)} className="nb-btn px-2 py-1 text-xs inline-flex items-center gap-1 text-white" style={{ background: "var(--retur)" }}><XCircle className="w-3.5 h-3.5" /> Cancel</button>
                      </>
                    )}
                  </div>
                </div>
              </div>
              <div className="mt-2 border-t-2 border-[#0A0A0A] pt-2 space-y-1">
                {s.lines.map((l, idx) => (
                  <div key={idx} className="flex justify-between text-sm">
                    <span>{l.item_name}{l.warna ? ` (${l.warna})` : ""} × {l.qty}</span>
                    <span className="text-[#52525B]">{rupiah(l.subtotal)}</span>
                  </div>
                ))}
              </div>
            </div>
          ))}
        </div>
      )}

      <Modal open={open} onClose={() => { setOpen(false); resetForm(); }} title={editId ? "Edit Penjualan" : "Catat Penjualan"} testid="sale-modal">
        <form onSubmit={save}>
          <div className="grid grid-cols-2 gap-3">
            <Field label="Tanggal"><input data-testid="sale-date" type="date" required value={date} onChange={(e) => setDate(e.target.value)} className="nb-input" /></Field>
            <Field label="Pelanggan"><input data-testid="sale-customer" value={customer} onChange={(e) => setCustomer(e.target.value)} placeholder="Umum" className="nb-input" /></Field>
          </div>
          <Field label="No. WhatsApp Pembeli (opsional)">
            <input data-testid="sale-customer-phone" value={customerPhone} onChange={(e) => setCustomerPhone(e.target.value)} placeholder="08…" inputMode="tel" className="nb-input" />
          </Field>
          <Field label="Pembelian via (Marketplace / Offline)">
            <select data-testid="sale-tipe" value={tipe} onChange={(e) => setTipe(e.target.value)} className="nb-input">
              {["Offline", "Shopee", "Tokopedia", "TikTok Shop", "Lazada", "Lainnya"].map((t) => <option key={t} value={t}>{t}</option>)}
            </select>
          </Field>
          <label className="label-tag block mb-1.5">Barang Terjual</label>
          <div className="space-y-3 mb-3">
            {lines.map((l, idx) => {
              const price = linePrice(l);
              const gross = price * Number(l.qty || 0);
              const linePjk = Math.round(Math.max(0, gross + Number(l.extra_bubble || 0) - Number(l.disc || 0)) * Number(l.pajak_pct || 0) / 100);
              const lineTotal = gross + Number(l.extra_bubble || 0) - Number(l.pot_mp || 0) - Number(l.disc || 0) - linePjk;
              const variants = variantsFor(l.name);
              return (
                <div key={idx} className="border-2 border-[#0A0A0A] rounded-lg p-3 space-y-2">
                  <div className="flex gap-2">
                    <div className="flex-1 grid grid-cols-2 gap-2">
                      <NameCombo testid={`sale-item-${idx}`} value={l.name} names={names} onSelect={(n) => onNameChange(idx, n)} />
                      <select data-testid={`sale-warna-${idx}`} value={l.item_id} onChange={(e) => setLine(idx, { item_id: e.target.value, price: itemMap[e.target.value]?.sell_price ?? "" })} className="nb-input" disabled={!l.name}>
                        <option value="">— Pilih warna —</option>
                        {variants.map((i) => <option key={i.id} value={i.id}>{i.warna || "Tanpa warna"} (stok {i.stock})</option>)}
                      </select>
                    </div>
                    {lines.length > 1 && (
                      <button type="button" onClick={() => removeLine(idx)} className="w-10 border-2 border-[#0A0A0A] rounded-lg flex items-center justify-center shrink-0" style={{ color: "var(--retur)" }}><Trash2 className="w-4 h-4" /></button>
                    )}
                  </div>
                  <div className="grid grid-cols-2 gap-2">
                    <div><label className="label-tag block mb-1">Harga Jual</label><input data-testid={`sale-price-${idx}`} type="number" min="0" value={l.price === "" || l.price == null ? "" : l.price} placeholder={String(itemMap[l.item_id]?.sell_price || 0)} onChange={(e) => setLine(idx, { price: e.target.value })} className="nb-input" /></div>
                    <div><label className="label-tag block mb-1">Qty</label><input data-testid={`sale-qty-${idx}`} type="number" min="1" value={l.qty} onChange={(e) => setLine(idx, { qty: e.target.value })} className="nb-input" /></div>
                    <div><label className="label-tag block mb-1">Extra Bubble</label><input data-testid={`sale-extra-${idx}`} type="number" min="0" value={l.extra_bubble || 0} onChange={(e) => setLine(idx, { extra_bubble: e.target.value })} className="nb-input" /></div>
                    <div><label className="label-tag block mb-1">Pot MP</label><input data-testid={`sale-pot-${idx}`} type="number" min="0" value={l.pot_mp || 0} onChange={(e) => setLine(idx, { pot_mp: e.target.value })} className="nb-input" /></div>
                    <div><label className="label-tag block mb-1">Disc/Voucher</label><input data-testid={`sale-disc-${idx}`} type="number" min="0" value={l.disc || 0} onChange={(e) => setLine(idx, { disc: e.target.value })} className="nb-input" /></div>
                    {canHpp && (
                      <div><label className="label-tag block mb-1">HPP (opsional)</label><input data-testid={`sale-hpp-${idx}`} type="number" min="0" value={l.buy_price === "" || l.buy_price == null ? "" : l.buy_price} placeholder={String(itemMap[l.item_id]?.buy_price ?? 0)} onChange={(e) => setLine(idx, { buy_price: e.target.value })} className="nb-input" /></div>
                    )}
                  </div>
                  <div className="flex items-center gap-1.5 flex-wrap">
                    <span className="label-tag">Diskon cepat:</span>
                    {[5, 10, 15].map((p) => (
                      <button type="button" key={p} data-testid={`sale-disc-pct-${idx}-${p}`} onClick={() => setLine(idx, { disc: Math.round(gross * p / 100) })} className="px-2.5 py-1 text-xs font-bold border-2 border-[#0A0A0A] rounded-md bg-white nb-hover" disabled={!gross}>{p}%</button>
                    ))}
                    <button type="button" data-testid={`sale-disc-clear-${idx}`} onClick={() => setLine(idx, { disc: 0 })} className="px-2.5 py-1 text-xs font-bold border-2 border-[#0A0A0A] rounded-md bg-white nb-hover">Hapus</button>
                  </div>
                  <div className="flex items-center gap-1.5 flex-wrap">
                    <span className="label-tag">Pajak:</span>
                    {[0.5, 1, 2].map((p) => (
                      <button type="button" key={p} data-testid={`sale-pajak-pct-${idx}-${p}`} onClick={() => setLine(idx, { pajak_pct: p })} className={`px-2.5 py-1 text-xs font-bold border-2 border-[#0A0A0A] rounded-md nb-hover ${Number(l.pajak_pct) === p ? "text-[#0A0A0A]" : "bg-white"}`} style={Number(l.pajak_pct) === p ? { background: "var(--primary)" } : {}} disabled={!gross}>{String(p).replace(".", ",")}%</button>
                    ))}
                    <button type="button" data-testid={`sale-pajak-clear-${idx}`} onClick={() => setLine(idx, { pajak_pct: 0 })} className="px-2.5 py-1 text-xs font-bold border-2 border-[#0A0A0A] rounded-md bg-white nb-hover">Hapus</button>
                    {Number(l.pajak_pct) > 0 && <span className="text-xs text-[#52525B]">− {rupiah(linePjk)}</span>}
                  </div>
                  <div className="flex justify-between text-sm border-t border-[#0A0A0A]/20 pt-2">
                    <span className="text-[#52525B]">Harga {rupiah(gross)}</span>
                    <span className="font-bold">Total {rupiah(lineTotal)}</span>
                  </div>
                </div>
              );
            })}
          </div>
          <button type="button" data-testid="add-sale-line" onClick={addLine} className="text-sm font-bold underline mb-4">+ Tambah barang</button>
          <div className="border-t-2 border-[#0A0A0A] pt-3 mb-3 space-y-1">
            <div className="flex justify-between text-sm"><span className="label-tag">Harga Kotor</span><span className="font-bold">{rupiah(grossSum)}</span></div>
            <div className="flex justify-between text-sm"><span className="label-tag">Extra Bubble</span><span className="font-bold">+ {rupiah(extraSum)}</span></div>
            <div className="flex justify-between text-sm"><span className="label-tag">Potongan MP</span><span className="font-bold">- {rupiah(potSum)}</span></div>
            <div className="flex justify-between text-sm"><span className="label-tag">Disc / Voucher</span><span className="font-bold">- {rupiah(discSum)}</span></div>
            <div className="flex justify-between text-sm"><span className="label-tag">Pajak</span><span className="font-bold">- {rupiah(pajakSum)}</span></div>
            <div className="flex justify-between items-center"><span className="label-tag">Total Harga</span><span className="font-display font-extrabold text-2xl">{rupiah(netSum)}</span></div>
          </div>
          <button data-testid="save-sale-button" type="submit" className="nb-btn w-full py-3 text-[#0A0A0A]" style={{ background: "var(--primary)" }}>{editId ? "Simpan Perubahan" : "Simpan Penjualan"}</button>
        </form>
      </Modal>

      <Modal open={notaOpen} onClose={() => setNotaOpen(false)} title="Nota Pembeli" testid="nota-modal">
        {notaSale && (
          <div>
            <div ref={notaRef} className="rounded-2xl border-2 border-[#0A0A0A] overflow-hidden" style={{ background: "#FFFDF7" }} data-testid="nota-preview">
              <div className="p-3 text-center" style={{ background: "var(--primary)" }}>
                <img src="/logo.png" alt="Mainan Dede" className="h-12 mx-auto object-contain" onError={(e) => { e.currentTarget.style.display = "none"; }} />
              </div>
              <div className="px-4 pt-3 text-center">
                <div className="font-display font-extrabold text-xl">{STORE.name}</div>
                <p className="text-[11px] text-[#52525B] leading-snug mt-1">{STORE.address}</p>
                <p className="text-[11px] text-[#52525B]">Telp: {STORE.phone}</p>
              </div>
              <div className="px-4 mt-3 flex justify-between text-xs text-[#52525B]">
                <span>🗓️ {notaSale.date}</span><span>{notaSale.customer || "Umum"} · {notaSale.tipe || "Offline"}</span>
              </div>
              <div className="px-4 mt-2">
                <table className="w-full text-xs">
                  <thead><tr className="border-y-2 border-[#0A0A0A]">
                    <th className="text-left py-1.5">No</th><th className="text-left">Barang</th><th className="text-center">Qty</th><th className="text-right">Harga</th><th className="text-right">Disc</th><th className="text-right">Total</th>
                  </tr></thead>
                  <tbody>
                    {notaSale.lines.map((l, i) => {
                      const unit = l.price != null ? l.price : (l.qty ? ((l.harga || l.subtotal) / l.qty) : 0);
                      const disc = (l.pot_mp || 0) + (l.disc || 0);
                      return (
                        <tr key={i} className="border-b border-[#0A0A0A]/10">
                          <td className="py-1.5">{i + 1}</td>
                          <td>{l.item_name}{l.warna ? ` (${l.warna})` : ""}</td>
                          <td className="text-center">{l.qty}</td>
                          <td className="text-right">{rupiah(unit)}</td>
                          <td className="text-right">{disc ? rupiah(disc) : "-"}</td>
                          <td className="text-right">{rupiah(l.subtotal)}</td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
              <div className="px-4 mt-1 flex justify-between items-center border-t-2 border-[#0A0A0A] pt-2">
                <span className="font-bold text-sm">TOTAL</span>
                <span className="font-display font-extrabold text-lg">{rupiah(notaSale.net ?? notaSale.total)}</span>
              </div>
              <div className="m-4 rounded-xl p-3 text-xs leading-relaxed" style={{ background: "#fff", border: "2px dashed #0A0A0A" }}>
                <b>💳 Pembayaran</b><br />{STORE.bank}
              </div>
            </div>

            <div className="mt-4">
              <label className="label-tag block mb-1.5">No. WhatsApp Pembeli</label>
              <input data-testid="nota-phone" value={buyerPhone} onChange={(e) => setBuyerPhone(e.target.value)} placeholder="08…" inputMode="tel" className="nb-input w-full" />
              <p className="text-[11px] text-[#52525B] mt-1">Kirim foto: pilih WhatsApp di menu berbagi, lalu pilih kontak & kirim.</p>
              {notaSale.sent_at && (
                <p data-testid="nota-sent-info" className="text-[11px] font-bold mt-1" style={{ color: "#1a7a43" }}>✓ Sudah dikirim {notaSale.sent_count || 1}× · terakhir {fmtSent(notaSale.sent_at)}</p>
              )}
            </div>
            <div className="grid grid-cols-3 gap-2 mt-3">
              <button data-testid="nota-send-photo" onClick={shareNotaImage} disabled={sharing} className="nb-btn py-2.5 text-white text-sm flex items-center justify-center gap-1.5 disabled:opacity-60" style={{ background: "#25D366" }}><MessageCircle className="w-4 h-4" /> {sharing ? "Menyiapkan…" : "Kirim Foto"}</button>
              <button data-testid="nota-send-wa-text" onClick={sendWAText} className="nb-btn py-2.5 bg-white text-sm flex items-center justify-center gap-1.5"><MessageCircle className="w-4 h-4" /> Teks WA</button>
              <button data-testid="nota-print" onClick={() => printReceipt(notaSale)} className="nb-btn py-2.5 text-[#0A0A0A] text-sm flex items-center justify-center gap-1.5" style={{ background: "var(--primary)" }}><Printer className="w-4 h-4" /> Cetak</button>
            </div>
          </div>
        )}
      </Modal>
    </div>
  );
}
